import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Dropdown, DropdownButton, Row} from "react-bootstrap";
import { SearchIcon } from '@primer/octicons-react'
import LocPickerDialog from './LocPickerDialog';
import LiftPickerDialog from './LiftPickerDialog';
import { axiosAuth_DATA_MGMT_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';


const CCTVCamAdmin_NewCCTVCamView = (props) => {

  // Utilities
  const { t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  // Initialize with actual JSON obj.  If not, will get error.
  const [cctvCamAttrs, setCCTVCamAttrs] = useState({
    code: '',
    description: '',
    displayName: '',
    apiCode: '',
    assetCode: '',
    locationId: '',
    location: '',
    coverageLocId: '',
    coverageLoc: '',
    spaceType: '',
    spaceSubType: '',
    liftId: '',
    liftCode: '',
    status: ''
  });

  const statusOptions = [
    { key: "ACTIVE",    value: "ACTIVE" },
    { key: "DISABLED", value: "DISABLED" }
  ];

  const spaceTypeOptions = [
    { key: "LOCATION", value: "LOCATION" },
    { key: "LIFT",     value: "LIFT" }
  ];

  const spaceSubTypeOptions = [
    { key: "CAB",       value: "CAB" },
    { key: "LOWER_CAB", value: "LOWER_CAB" },
    { key: "UPPER_CAB", value: "UPPER_CAB" }
  ];


  // UI Functions

  // Location picker dialog stuff

  const [locPickerMode,       setLocPickerMode]       = useState('');
  const [showLocPickerDialog, setShowLocPickerDialog] = useState(false);


  const handleLocPickerLoc = (e) => {
    setLocPickerMode('LOC');
    setShowLocPickerDialog(true);
  }

  const handleLocPickerCoverLoc = (e) => {
    setLocPickerMode('COVER_LOC');
    setShowLocPickerDialog(true);
  }

  function handleLocPickerClose() {
    setShowLocPickerDialog(false);
  }

  function handleLocSelect(location) {

    // Deep copy
    let newAttr = JSON.parse (JSON.stringify(cctvCamAttrs));

    if (locPickerMode === 'LOC') {
      newAttr.locationId = location.id;
      newAttr.location   = location.code;
    } else if (locPickerMode === 'COVER_LOC') {
      newAttr.coverageLocId = location.id;
      newAttr.coverageLoc   = location.code;
    }

    setCCTVCamAttrs(newAttr);

    setShowLocPickerDialog(false);
  }


  // Lift picker dialog stuff

  const [showLiftPickerDialog, setShowLiftPickerDialog] = useState(false);

  const handleLiftPicker = (e) => {
    setShowLiftPickerDialog(true);
  }

  function handleLiftPickerClose() {
    setShowLiftPickerDialog(false);
  }

  function handleLiftSelect(lift) {

    // Deep copy
    let newAttr = JSON.parse (JSON.stringify(cctvCamAttrs));

    newAttr.liftId   = lift.id;
    newAttr.liftCode = lift.code;

    setCCTVCamAttrs(newAttr);

    setShowLiftPickerDialog(false);
  }


  // General stuff

  const [errMsg, setErrMsg] = useState('');

  const onAttrsInputChange = (e) => {
    setCCTVCamAttrs({ ...cctvCamAttrs, [e.target.name]: e.target.value })
  }

  const onStatusSelect = (key, event) => {
    setCCTVCamAttrs({ ...cctvCamAttrs, status: key })
  };

  const onSpaceTypeSelect = (key, event) => {
    setCCTVCamAttrs({ ...cctvCamAttrs, spaceType: key })
  };

  const onSpaceSubTypeSelect = (key, event) => {
    setCCTVCamAttrs({ ...cctvCamAttrs, spaceSubType: key })
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!cctvCamAttrs.code || cctvCamAttrs.code.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_CCTV_cam_code"));
    } else {
      validateCode();
    }

    return;
  }

  // Since this is async, need to call this first, and check the others when username availability returns
  function validateCode() {

    new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.get("cctv-cams?code=" + cctvCamAttrs.code);

        resolve(res.data);

        if ((res.status === 200) && res.data.length > 0) {
          // CCTVCam code is already defined
          setErrMsg(t("I18N_ERR_CCTV_cam_code_exists"));
          return;
        } else {
          validateOtherFields();
          return;
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
          validateOtherFields();
        }
        return;
      }
    });
  }

  function validateOtherFields() {
    // Validate form inputs

    if (cctvCamAttrs.code.length > 30) {
      setErrMsg(t("I18N_ERR_Max_len_code") + 30);
      return;
    } else if (!cctvCamAttrs.description || cctvCamAttrs.description.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_description"));
      return;
    } else if (cctvCamAttrs.description.length > 200) {
      setErrMsg(t("I18N_ERR_Max_len_desc") + 200);
      return;
    } else if (!cctvCamAttrs.displayName || cctvCamAttrs.displayName.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_display_name"));
      return;
    } else if (cctvCamAttrs.displayName.length > 30) {
      setErrMsg(t("I18N_ERR_Max_len_disp_name") + 30);
      return;
    } else if (!cctvCamAttrs.apiCode || cctvCamAttrs.apiCode.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_API_code"));
      return;
    } else if (cctvCamAttrs.apiCode.length > 200) {
      setErrMsg(t("I18N_ERR_Max_len_api_code") + 200);
      return;
    } else if (!cctvCamAttrs.assetCode || cctvCamAttrs.assetCode.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_asset_id"));
      return;
    } else if (cctvCamAttrs.assetCode.length > 50) {
      setErrMsg(t("I18N_ERR_Max_len_asset_code") + 50);
      return;
    } else if (!cctvCamAttrs.status || cctvCamAttrs.status.length === 0) {
      setErrMsg(t("I18N_ERR_Need_status"));
      return;
    } else if (!cctvCamAttrs.spaceType || cctvCamAttrs.spaceType.length === 0) {
      setErrMsg(t("I18N_ERR_Need_space_type"));
      return;
    }

    if (cctvCamAttrs.spaceType === 'LOCATION') {
      if (!cctvCamAttrs.locationId || cctvCamAttrs.locationId.length === 0) {
        setErrMsg(t("I18N_ERR_Need_location_code"));
        return;
      } else if (!cctvCamAttrs.coverageLocId || cctvCamAttrs.coverageLocId.length === 0) {
        setErrMsg(t("I18N_ERR_Need_cov_loc_code"));
        return;
      }
    } else if (cctvCamAttrs.spaceType === 'LIFT') {
      if (!cctvCamAttrs.spaceSubType || cctvCamAttrs.spaceSubType.length === 0) {
        setErrMsg(t("I18N_ERR_Need_space_sub_type"));
        return;
      } else if (!cctvCamAttrs.liftCode || cctvCamAttrs.liftCode.length === 0) {
        setErrMsg(t("I18N_ERR_Need_lift_code"));
        return;
      }
    }

    createNewCCTVCam();
  }

  const createNewCCTVCam = () => {

    // Make a deep copy
    const reqBody = JSON.parse(JSON.stringify(cctvCamAttrs));

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.post("cctv-cams", reqBody);

        resolve(res.data);

        if (res.status === 201) {

          // Return to list
          navigate(-1);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }


  const handleCancel = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_New_CCTVCam")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_CCTVCam_info")}
            </span>
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>
        </div>

        <Container>
          <form>
            <Row className={classes.admin_pages__field_label}>
              <Col md={3}>{t("I18N_Code")}: *</Col>
              <Col md={3}>{t("I18N_Description")}: *</Col>
              <Col md={3}>{t("I18N_Display_name")}: *</Col>
              <Col md={3}>{t("I18N_API_code")}: *</Col>
            </Row>
            <Row className={classes.admin_pages__field_value}>
              <Col md={3}>
                <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="code" value={cctvCamAttrs.code}
                  onChange={(e) => onAttrsInputChange(e)} />
              </Col>
              <Col md={3}>
                <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="description"
                          value={cctvCamAttrs.description} onChange={(e) => onAttrsInputChange(e)} />
              </Col>
              <Col md={3}>
                <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="displayName" value={cctvCamAttrs.displayName}
                  onChange={(e) => onAttrsInputChange(e)} />
              </Col>
              <Col md={3}>
                <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="apiCode" value={cctvCamAttrs.apiCode}
                  onChange={(e) => onAttrsInputChange(e)} />
              </Col>
            </Row>

            <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
              <Col md={3}>{t("I18N_Asset_code")}: *</Col>
              <Col md={3}>{t("I18N_Status")}: *</Col>
              <Col md={3}></Col>
              <Col md={3}></Col>
            </Row>
            <Row className={classes.admin_pages__field_value}>
              <Col md={3}>
                <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="assetCode" value={cctvCamAttrs.assetCode}
                  onChange={(e) => onAttrsInputChange(e)} />
              </Col>
              <Col md={3}>
                <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                  title={ !cctvCamAttrs.status ? t("common:I18N_DROPDOWN_PROMPT") : cctvCamAttrs.status }
                  onSelect={onStatusSelect}>
                  {statusOptions.map((item, index) => {
                    return (
                      <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                        {t("I18N_ST_" + item.value)}
                      </Dropdown.Item>
                    );
                  })}
                </DropdownButton>
              </Col>
              <Col md={3}></Col>
              <Col md={3}></Col>
            </Row>

            <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
              <Col md={3}>{t("I18N_Space_type")}: *</Col>
              { cctvCamAttrs.spaceType === 'LOCATION' ?
                <>
                  <Col md={3}>{t("I18N_Location_code")}: *</Col>
                  <Col md={3}>{t("I18N_Coverage_loc_code")}: *</Col>
                </>
                : cctvCamAttrs.spaceType === 'LIFT' ?
                <>
                  <Col md={3}>{t("I18N_Space_subtype")}: *</Col>
                  <Col md={3}>{t("I18N_Lift_code")}: *</Col>
                </>
                : null
              }
              <Col md={3}></Col>
            </Row>
            <Row className={classes.admin_pages__field_value}>
              <Col md={3}>
                <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                  title={ !cctvCamAttrs.spaceType ? t("common:I18N_DROPDOWN_PROMPT") : cctvCamAttrs.spaceType }
                  onSelect={onSpaceTypeSelect}>
                  {spaceTypeOptions.map((item, index) => {
                    return (
                      <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                        {t("I18N_CCTV_SP_TYPE_" + item.value)}
                      </Dropdown.Item>
                    );
                  })}
                </DropdownButton>
              </Col>
              { cctvCamAttrs.spaceType === 'LOCATION' ?
                <>
                 <Col md={3}>
                   <input readOnly className={classes.admin_pages__field_editable} style={{width:'80%'}} name="location" value={cctvCamAttrs.location}
                     onChange={(e) => onAttrsInputChange(e)} />
                   <span onClick={handleLocPickerLoc} style={{ marginLeft:'10px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                     <SearchIcon size={14} />
                   </span>
                 </Col>
                 <Col md={3}>
                   <input readOnly className={classes.admin_pages__field_editable} style={{width:'80%'}} name="coverageLoc" value={cctvCamAttrs.coverageLoc}
                     onChange={(e) => onAttrsInputChange(e)} />
                   <span onClick={handleLocPickerCoverLoc} style={{ marginLeft:'10px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                     <SearchIcon size={14} />
                   </span>
                 </Col>
                </>
              : cctvCamAttrs.spaceType === 'LIFT' ?
                <>
                  <Col md={3}>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={ !cctvCamAttrs.spaceSubType ? t("common:I18N_DROPDOWN_PROMPT") : cctvCamAttrs.spaceSubType }
                      onSelect={onSpaceSubTypeSelect}>
                      {spaceSubTypeOptions.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {item.value}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                  <Col md={3}>
                    <input readOnly className={classes.admin_pages__field_editable} style={{width:'80%'}} name="liftCode" value={cctvCamAttrs.liftCode}
                      onChange={(e) => onAttrsInputChange(e)} />
                    <span onClick={handleLiftPicker} style={{ marginLeft:'10px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                      <SearchIcon size={14} />
                    </span>
                  </Col>
                </>
                : null
              }
              <Col md={3}></Col>
            </Row>

            <hr className="solid" style={{ marginTop: '30px' }}></hr>

            <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px', fontStyle: 'italic' }}>
              <span>{t("I18N_Req_fields")}</span>
            </Row>

            <Row>
              <div display='flex' style={{ marginTop: '15px' }}>
                <Button size="sm" variant="outline-secondary" className="mb-3"
                  style={{ float: 'left' }}
                  onClick={handleCancel}>
                  {t("I18N_Cancel")}
                </Button>
                <Button size="sm" variant="outline-secondary" className="mb-3"
                  style={{ marginLeft: '15px' }}
                  onClick={handleSubmit}>
                  {t("I18N_Submit")}
                </Button>
                <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                  {errMsg}
                </span>
              </div>
            </Row>
          </form>
        </Container>


        <LocPickerDialog
          show={showLocPickerDialog}
          handleObjSelect={handleLocSelect}
          onClose={handleLocPickerClose}
        />

        <LiftPickerDialog
          show={showLiftPickerDialog}
          handleObjSelect={handleLiftSelect}
          onClose={handleLiftPickerClose}
        />

      </Card>
    </>
  );
}

export default CCTVCamAdmin_NewCCTVCamView;
